import { join, relative } from "node:path";

import { z } from "zod";

import { CliError } from "../errors/index.js";
import { type BoardMetadata, BoardMetadataSchema } from "../model/board.js";
import {
  type BoardProfile,
  CURRENT_PROTOCOL_VERSION,
  SessionKeySchema,
} from "../model/common.js";
import type { ExecutionContext } from "../runtime/context.js";
import {
  ensureDirectory,
  readJsonFile,
  walkFiles,
  writeJsonFileAtomic,
  writeJsonFileExclusive,
} from "../runtime/filesystem.js";
import { getPackageVersion } from "../runtime/package-version.js";
import { detectRuntimeName } from "../runtime/sqlite.js";

const BOARD_FILE_NAME = "board.json";

const LooseBoardMetadataSchema = z
  .object({
    dict: z.record(z.string(), z.string()).default({}),
    sessionKey: z.string(),
    status: z.string().optional(),
    version: z.object({
      package: z.string(),
      protocol: z.number().int(),
    }),
  })
  .passthrough();

function normalizeRelativePath(value: string) {
  return value.replaceAll("\\", "/");
}

export function sessionKeyToSegments(sessionKey: string) {
  return SessionKeySchema.parse(sessionKey).split("/");
}

export function boardDirectory(context: ExecutionContext, sessionKey: string) {
  return join(context.storageRoot, "boards", ...sessionKeyToSegments(sessionKey));
}

export function boardMetadataPath(context: ExecutionContext, sessionKey: string) {
  return join(boardDirectory(context, sessionKey), BOARD_FILE_NAME);
}

function defaultBoardDict() {
  return {
    $ARTIFACTS: "artifacts",
    $BOARD: ".",
    $META: BOARD_FILE_NAME,
    $STORE: "store.db",
  };
}

export async function createBoardMetadataClaim(
  context: ExecutionContext,
  sessionKey: string,
  profile: BoardProfile,
) {
  const now = new Date().toISOString();
  const metadata: BoardMetadata = {
    branch: context.branch,
    createdAt: now,
    cwd: context.cwd,
    dict: defaultBoardDict(),
    gitDir: context.gitDir,
    profile,
    projectRoot: context.projectRoot,
    rootPositionId: "pending",
    runtime: detectRuntimeName(),
    selectedPositionId: undefined,
    sessionKey: SessionKeySchema.parse(sessionKey),
    status: "creating",
    tipPositionId: "pending",
    updatedAt: now,
    version: {
      package: await getPackageVersion(),
      protocol: CURRENT_PROTOCOL_VERSION,
    },
  };

  try {
    await ensureDirectory(boardDirectory(context, sessionKey));
    await writeJsonFileExclusive(boardMetadataPath(context, sessionKey), metadata);
  } catch (error) {
    if (isAlreadyExists(error)) {
      throw new CliError("BOARD_ALREADY_EXISTS", `Board "${sessionKey}" already exists`);
    }
    throw error;
  }

  return metadata;
}

export async function readBoardMetadataRaw(
  context: ExecutionContext,
  sessionKey: string,
) {
  const raw = await readJsonFile<unknown>(boardMetadataPath(context, sessionKey));
  if (raw === undefined) {
    return undefined;
  }

  const parsed = LooseBoardMetadataSchema.safeParse(raw);
  if (!parsed.success) {
    throw new CliError(
      "BOARD_CORRUPTED",
      `Board "${sessionKey}" metadata is unreadable`,
      parsed.error.flatten(),
    );
  }
  return parsed.data;
}

export async function loadBoardMetadata(
  context: ExecutionContext,
  sessionKey: string,
) {
  const metadata = await readJsonFile<unknown>(boardMetadataPath(context, sessionKey));
  if (metadata === undefined) {
    throw new CliError(
      "BOARD_NOT_FOUND",
      `Board "${sessionKey}" does not exist for ${context.projectRoot}`,
    );
  }

  const parsed = BoardMetadataSchema.safeParse(metadata);
  if (!parsed.success) {
    throw new CliError(
      "BOARD_CORRUPTED",
      `Board "${sessionKey}" metadata does not match protocol v${CURRENT_PROTOCOL_VERSION}`,
      parsed.error.flatten(),
    );
  }

  return parsed.data;
}

export async function requireReadyBoardMetadata(
  context: ExecutionContext,
  sessionKey: string,
) {
  const metadata = await loadBoardMetadata(context, sessionKey);
  if (metadata.status !== "ready") {
    throw new CliError("BOARD_CORRUPTED", `Board "${sessionKey}" is not ready`, {
      status: metadata.status,
    });
  }
  return metadata;
}

export async function finalizeBoardMetadata(
  context: ExecutionContext,
  sessionKey: string,
  input: {
    rootPositionId: string;
    tipPositionId: string;
  },
) {
  return await updateBoardMetadata(context, sessionKey, (metadata) => ({
    ...metadata,
    rootPositionId: input.rootPositionId,
    selectedPositionId: input.rootPositionId,
    status: "ready",
    tipPositionId: input.tipPositionId,
  }));
}

export async function updateBoardMetadata(
  context: ExecutionContext,
  sessionKey: string,
  mutate: (metadata: BoardMetadata) => BoardMetadata,
) {
  const metadata = await loadBoardMetadata(context, sessionKey);
  const next = BoardMetadataSchema.parse({
    ...mutate(metadata),
    updatedAt: new Date().toISOString(),
  });
  await writeJsonFileAtomic(boardMetadataPath(context, sessionKey), next);
  return next;
}

export async function listBoardMetadata(context: ExecutionContext) {
  const boardFiles = await walkFiles(join(context.storageRoot, "boards"), BOARD_FILE_NAME);
  const boards: BoardMetadata[] = [];
  for (const file of boardFiles) {
    const metadata = await readJsonFile<unknown>(file);
    const parsed = BoardMetadataSchema.safeParse(metadata);
    if (parsed.success) {
      boards.push(parsed.data);
    }
  }
  return boards.sort((left, right) =>
    left.updatedAt < right.updatedAt ? 1 : left.updatedAt > right.updatedAt ? -1 : 0,
  );
}

export function compressBoardRelativePath(
  metadata: Pick<BoardMetadata, "dict">,
  relativePath: string,
) {
  const normalized = normalizeRelativePath(relativePath);
  const aliases = Object.entries(metadata.dict).sort(
    (left, right) => right[1].length - left[1].length,
  );

  for (const [key, value] of aliases) {
    const normalizedValue = normalizeRelativePath(value);
    if (normalized === normalizedValue) {
      return key;
    }
    if (normalized.startsWith(`${normalizedValue}/`)) {
      return `${key}${normalized.slice(normalizedValue.length)}`;
    }
  }

  return normalized;
}

export function compressBoardPath(
  context: ExecutionContext,
  metadata: Pick<BoardMetadata, "dict" | "sessionKey">,
  absolutePath: string,
) {
  const relativePath = relative(
    boardDirectory(context, metadata.sessionKey),
    absolutePath,
  );
  return compressBoardRelativePath(metadata, relativePath);
}

function isAlreadyExists(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    error.code === "EEXIST"
  );
}
